import React from 'react';
import { Field, Form, Formik, FormikHelpers } from 'formik';

import Button from '@material-ui/core/Button';
import Dialog, { DialogProps } from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DialogPaper from '../DialogPaper';

import * as StampCardService from '../../api/stampCardService';
import StampGroupSelector from '../FormikAdapter/StampGroupSelector';
import useCompanyContext from '../../hooks/useCompanyContext';

export interface Values {
  stampNum: string;
}

const useStyles = makeStyles((theme) => ({
  buttonContainer: {
    width: 150,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  cardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
    width: '100%',
  },
  title: {
    marginTop: theme.spacing(3),
    fontWeight: theme.typography.fontWeightBold,
    textAlign: 'center',
  },
  subtext: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    fontSize: 21,
  },
  avatar: {
    height: 80,
    width: 80,
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3),
  },
  textField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  grow: {
    flexGrow: 1,
  },
}));

type Props = Pick<DialogProps, 'open' | 'onClose'> & {
  confirmRedeem: (addedStamp: number) => void;
  stampCardId: string;
  stampCampaignType: string;
  pin: string;
  maxSelectedNumber: number;
};

export default function AddStampDialog({
  onClose,
  open,
  confirmRedeem,
  stampCardId,
  pin,
  maxSelectedNumber,
}: Props) {
  const { company } = useCompanyContext();
  const classes = useStyles();

  const initialValues: Values = {
    stampNum: '1',
  };

  const handleSubmit = async (values: Values, _: FormikHelpers<Values>) => {
    try {
      if (company) {
        let stampAdded = parseInt(values.stampNum, 10);
        await StampCardService.credit({
          companyId: company.id,
          stampCardId,
          numOfStamps: stampAdded,
          pin,
        });
        confirmRedeem(stampAdded);
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperComponent={DialogPaper}>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={false}
      >
        {({ isSubmitting, values: { stampNum } }) => {
          return (
            <Form noValidate autoComplete="off">
              <div className={classes.cardContainer}>
                <Typography variant="h5" className={classes.title} gutterBottom>
                  Number of Stamps to Add
                </Typography>
                <Field
                  component={StampGroupSelector}
                  name="stampNum"
                  maxStamp={maxSelectedNumber}
                />
                <Typography className={classes.subtext} gutterBottom>
                  {`ADD ${stampNum} STAMP${
                    parseInt(stampNum, 10) > 1 ? 'S' : ''
                  }`}
                </Typography>
                <Button
                  type="submit"
                  color="primary"
                  className={classes.buttonContainer}
                  disabled={isSubmitting}
                >
                  CONFIRM
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
}
